/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, AlertTriangle, Info } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { notificheAPI } from '../api/notifiche';
import { fmtDataOra } from '../utils/date';

const ACCENT = '#8B5CF6';

export default function Notifiche() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: notifiche = [], isLoading } = useQuery({ queryKey: ['notifiche'], queryFn: () => notificheAPI.lista() });

  const segnaLetta = useMutation({ mutationFn: (id) => notificheAPI.segnaLetta(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifiche'] }) });
  const leggiTutte = useMutation({ mutationFn: () => notificheAPI.leggiTutte(), onSuccess: () => qc.invalidateQueries({ queryKey: ['notifiche'] }) });

  const nonLette = notifiche.filter((n) => !n.letta).length;

  const apri = (n) => {
    if (!n.letta) segnaLetta.mutate(n.id);
    if (n.link) navigate(n.link);
    else if (n.intervento_id) navigate(`/interventi/${n.intervento_id}`);
  };

  return (
    <AdminLayout title="Notifiche">
      {nonLette > 0 && (
        <button onClick={() => leggiTutte.mutate()} style={styles.leggiTutteBtn}>
          <CheckCheck size={16} /> Segna tutte come lette
        </button>
      )}
      {isLoading ? (
        <div style={styles.loading}>Caricamento...</div>
      ) : notifiche.length === 0 ? (
        <div style={styles.empty}><Bell size={40} color="#9CA3AF" /><p>Nessuna notifica al momento.</p></div>
      ) : (
        <div style={styles.list}>
          {notifiche.map((n) => {
            const isWarn = n.tipo === 'warning';
            return (
              <div key={n.id} onClick={() => apri(n)} style={{ ...styles.card, backgroundColor: n.letta ? 'white' : '#F3E8FF', cursor: (n.link || n.intervento_id) ? 'pointer' : 'default' }}>
                <div style={{ ...styles.iconBox, backgroundColor: isWarn ? '#FEE2E2' : '#EDE9FE' }}>
                  {isWarn ? <AlertTriangle size={18} color="#DC2626" /> : <Info size={18} color={ACCENT} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.cardHead}>
                    <span style={styles.cardTitle}>{n.titolo}</span>
                    {!n.letta && <span style={styles.newDot} />}
                  </div>
                  <div style={styles.cardMsg}>{n.messaggio}</div>
                  <div style={styles.cardTime}>{fmtDataOra(n.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  leggiTutteBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', marginBottom: '1.25rem', color: ACCENT },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', borderRadius: '12px' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  card: { display: 'flex', gap: '0.85rem', padding: '1rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  iconBox: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  cardTitle: { fontWeight: 700, fontSize: '0.92rem' },
  newDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ACCENT, flexShrink: 0 },
  cardMsg: { fontSize: '0.85rem', color: '#4B5563', marginTop: '0.25rem' },
  cardTime: { fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.4rem' },
};
